
import { useMemo } from 'react';
import { SavedState } from '@/components/team-details/types/selector-types';

/**
 * Hook to filter football data based on selected country, league, and year
 * 
 * @param savedStates The array of saved states to filter
 * @param selectedCountry The currently selected country
 * @param selectedLeague The currently selected league
 * @param selectedYear The currently selected year
 * @returns Object containing available options and filtered states
 */
export const useFootballFilters = (
  savedStates: SavedState[] | undefined,
  selectedCountry: string | null,
  selectedLeague: string | null,
  selectedYear: string | null
) => {
  // Get unique countries from saved states
  const availableCountries = useMemo(() => {
    if (!savedStates || savedStates.length === 0) return [];
    
    // Use Set to automatically handle uniqueness
    const uniqueCountries = new Set<string>();
    
    // Add each non-null country to the set
    savedStates.forEach(state => {
      if (state.country) {
        uniqueCountries.add(state.country);
      }
    });
    
    // Convert set to sorted array
    return Array.from(uniqueCountries).sort();
  }, [savedStates]);

  // Get leagues for the selected country
  const availableLeagues = useMemo(() => {
    if (!savedStates || !selectedCountry) return [];
    
    const uniqueLeagues = new Set<string>();
    
    // Filter states by selected country and add each non-null league to the set
    savedStates.forEach(state => {
      if (state.country === selectedCountry && state.league) {
        uniqueLeagues.add(state.league);
      }
    });
    
    return Array.from(uniqueLeagues).sort();
  }, [savedStates, selectedCountry]);

  // Get years for the selected country and league
  const availableYears = useMemo(() => {
    if (!savedStates || !selectedCountry || !selectedLeague) return [];
    
    const uniqueYears = new Set<string>();
    
    // Filter states by selected country and league, then add each non-null year to the set
    savedStates.forEach(state => {
      if (state.country === selectedCountry && 
          state.league === selectedLeague && 
          state.year) {
        uniqueYears.add(state.year);
      }
    });
    
    // Convert set to array and sort in descending order (newest years first)
    return Array.from(uniqueYears).sort((a, b) => b.localeCompare(a));
  }, [savedStates, selectedCountry, selectedLeague]);

  // Filter states based on all selected filters
  const filteredStates = useMemo(() => {
    if (!savedStates) return [];
    
    return savedStates.filter(state => {
      // Start with all states
      let matches = true;
      
      // Apply each filter progressively if it's selected
      if (selectedCountry) {
        matches = matches && state.country === selectedCountry;
      }
      
      if (matches && selectedLeague) {
        matches = matches && state.league === selectedLeague;
      }
      
      if (matches && selectedYear) {
        matches = matches && state.year === selectedYear;
      }
      
      return matches;
    });
  }, [savedStates, selectedCountry, selectedLeague, selectedYear]);

  return {
    availableCountries,
    availableLeagues,
    availableYears,
    filteredStates
  };
};
